// pages/AdminVisitsList.tsx
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Loader2, Calendar, Building2, Clock, Files, File,
    Users, Eye, FileStack, CheckCircle2, XCircle, AlertCircle,
    MapPin, Pencil, ShieldCheck
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { fetchUsersWithProfiles, type CombinedUser } from '@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile';
import { AdminVisitsService } from '@/lib/collections/visits/admin/getVisitFetchAdmin';
import { useAuth } from '@/context/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'react-toastify';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MapboxMap, { parseCoords } from '@/ubication/mapbox';

interface Visit {
    $id: string;
    userId: string;
    organizationId: string;
    organizationName: string;
    organizationCity?: string;
    organizationAddress?: string;
    status: string;
    visitDates: string[];
    visitPurpose?: string | null;
    userComment?: string | null;
    $createdAt: string;

    branch?: string | null;
    contactPerson?: string | null;
    contactRole?: string | null;
    agreements?: string[];
    observations?: string[];
    signature?: string[];
    usersId?: string[] | null;

}

const AdminVisitsList = () => {
    const { userRoles } = useAuth();

    // Estados principales
    const [visits, setVisits] = useState<Visit[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [usersError, setUsersError] = useState<string | null>(null)

    // Estado de edición (solo admin)
    const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
    const [editForm, setEditForm] = useState({
        status: '',
        visitPurpose: '',
        branch: '',
        visitDate: '',
        startTime: '',
        endTime: '',
        companions: [] as string[],
    });
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    // Estados de filtros
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
    const [selectedUser, setSelectedUser] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const [page, setPage] = useState(1);
    const limit = 12;

    // Estados de usuarios
    const [users, setUsers] = useState<CombinedUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    // Estados de estadísticas
    const [globalStats, setGlobalStats] = useState({
        totalRecords: 0,
        totalUsers: 0,
        pending: 0,
        completed: 0,
        canceled: 0
    });

    const [userStats, setUserStats] = useState<{
        totalRecords: number;
        pending: number;
        completed: number;
        canceled: number;
    } | null>(null);

    // Estados para detalle
    const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
    const [showDetail, setShowDetail] = useState(false);

    // Cargar usuarios y estadísticas globales al montar
    useEffect(() => {
        loadUsers();
    }, []);

    useEffect(() => {
        loadGlobalStats();
    }, [selectedYear, selectedMonth]);


    // Cargar visitas cuando cambien los filtros
    useEffect(() => {
        loadVisits();
    }, [selectedYear, selectedMonth, selectedUser, page, searchQuery, statusFilter]);

    // Cargar estadísticas del usuario cuando cambie
    useEffect(() => {
        if (selectedUser !== 'all') {
            loadUserStats(selectedUser);
        } else {
            setUserStats(null);
        }
    }, [selectedUser, selectedYear, selectedMonth]);


    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            setUsersError(null);
            const userIdsWithVisits = await AdminVisitsService.getUsersWithVisits();
            const usersData = await fetchUsersWithProfiles();
            const usersWithVisits = usersData.filter(user =>
                userIdsWithVisits.includes(user.id) &&
                user.profile
                // && u.profile.isActive !== false //para usuarios 
            );
            setUsers(usersWithVisits);
        } catch (error) {
            console.error('Error loading users:', error);
            setUsersError('Error al cargar usuarios');
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadGlobalStats = async () => {
        try {
            let monthFilter: string | undefined = undefined;
            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const stats = await AdminVisitsService.getGlobalStats(monthFilter);
            setGlobalStats(stats);
        } catch (error) {
            console.error('Error loading global stats:', error);
        }
    };

    const loadUserStats = async (userId: string) => {
        try {
            let monthFilter: string | undefined = undefined;
            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const stats = await AdminVisitsService.getUserStats(userId, monthFilter);
            setUserStats(stats);
        } catch (error) {
            console.error('Error loading user stats:', error);
            setUserStats(null);
        }
    };

    const loadVisits = async () => {
        try {
            if (!searchQuery.trim()) {
                setLoading(true);
            }
            setError(null);

            let monthFilter: string | undefined = undefined;
            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const offset = (page - 1) * limit;
            const result = await AdminVisitsService.getAllVisits(
                {
                    userId: selectedUser,
                    month: monthFilter,
                    searchQuery: searchQuery,
                    statusFilter: statusFilter

                },
                limit,
                offset
            );

            setVisits(result.documents);
            setTotal(result.total);
        } catch {
            setError('No se pudo cargar la información. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = (visit: Visit) => {
        setSelectedVisit(visit);
        setShowDetail(true);
    };

    const handleOpenEdit = (visit: Visit, e?: React.MouseEvent) => {
        e?.stopPropagation();

        const toLocalDate = (iso: string) => {
            const d = new Date(iso);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        const toLocalTime = (iso: string) => {
            const d = new Date(iso);
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        };

        const startRaw = visit.visitDates[1];
        const endRaw   = visit.visitDates[2];

        setEditForm({
            status:       visit.status,
            visitPurpose: visit.visitPurpose ?? '',
            branch:       visit.branch ?? '',
            visitDate:    startRaw ? toLocalDate(startRaw) : '',
            startTime:    startRaw ? toLocalTime(startRaw) : '',
            endTime:      endRaw   ? toLocalTime(endRaw)   : '',
            companions:   visit.usersId ?? [],
        });
        setEditError(null);
        setEditingVisit(visit);
    };

    const handleSaveEdit = async () => {
        if (!editingVisit) return;

        // Validar horario
        if (editForm.startTime && editForm.endTime && editForm.startTime >= editForm.endTime) {
            setEditError('La hora de salida debe ser posterior a la hora de entrada');
            return;
        }

        setIsSaving(true);
        setEditError(null);
        try {
            // Reconstruir visitDates preservando la fecha de creación [0]
            const newVisitDates = [...(editingVisit.visitDates ?? [])];
            if (editForm.visitDate && editForm.startTime) {
                newVisitDates[1] = new Date(`${editForm.visitDate}T${editForm.startTime}:00`).toISOString();
            }
            if (editForm.visitDate && editForm.endTime) {
                newVisitDates[2] = new Date(`${editForm.visitDate}T${editForm.endTime}:00`).toISOString();
            }

            await AdminVisitsService.updateVisit(editingVisit.$id, {
                status:       editForm.status,
                visitPurpose: editForm.visitPurpose.trim() || null,
                branch:       editForm.branch.trim() || null,
                visitDates:   newVisitDates,
                usersId:      editForm.companions.length > 0 ? editForm.companions : null,
            });

            const updated = {
                status:       editForm.status,
                visitPurpose: editForm.visitPurpose.trim() || null,
                branch:       editForm.branch.trim() || null,
                visitDates:   newVisitDates,
                usersId:      editForm.companions.length > 0 ? editForm.companions : null,
            };
            setVisits(prev => prev.map(v => v.$id === editingVisit.$id ? { ...v, ...updated } : v));
            if (selectedVisit?.$id === editingVisit.$id) {
                setSelectedVisit(prev => prev ? { ...prev, ...updated } : prev);
            }
            setEditingVisit(null);
            toast.success('Visita actualizada correctamente');
        } catch (err: any) {
            setEditError(err.message || 'Error al guardar los cambios');
        } finally {
            setIsSaving(false);
        }
    };

    const getUserName = (userId: string) => {
        const user = users.find(u => u.id === userId);
        return user?.name || 'Usuario desconocido';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-EC', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('es-EC', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status: string) => {
        const variants = {
            pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pendiente', icon: AlertCircle },
            completed: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Completada', icon: CheckCircle2 },
            canceled: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Cancelada', icon: XCircle }
        };
        const variant = variants[status as keyof typeof variants] || variants.pending;
        const Icon = variant.icon;



        return (
            <Badge className={`${variant.color} border flex items-center gap-1`}>
                <Icon className="h-3 w-3" />
                {variant.label}
            </Badge>
        );
    };

    const getSignatureUrl = (fileId: string): string => {
        const BUCKET_ID = import.meta.env.VITE_BUCKET_ID_SIGNATURES_REQUEST_VISIT;
        return `${import.meta.env.VITE_APPWRITE_ENDPOINT}/storage/buckets/${BUCKET_ID}/files/${fileId}/view?project=${import.meta.env.VITE_APPWRITE_PROJECT_ID}`;
    };

    const getYearOptions = () => {
        const options = [];
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push({ value: year.toString(), label: year.toString() });
        }
        return options;
    };

    const getMonthOptions = () => {
        return [
            { value: '01', label: 'Enero' },
            { value: '02', label: 'Febrero' },
            { value: '03', label: 'Marzo' },
            { value: '04', label: 'Abril' },
            { value: '05', label: 'Mayo' },
            { value: '06', label: 'Junio' },
            { value: '07', label: 'Julio' },
            { value: '08', label: 'Agosto' },
            { value: '09', label: 'Septiembre' },
            { value: '10', label: 'Octubre' },
            { value: '11', label: 'Noviembre' },
            { value: '12', label: 'Diciembre' }
        ];
    };

    const totalPages = Math.ceil(total / limit);

    // Determinar qué estadísticas mostrar
    const displayStats = userStats || globalStats;
    const isUserSelected = selectedUser !== 'all';

    if (loading && visits.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Bitácoras</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Administrativo - Visitas</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden"><File className="w-5 h-5" /></BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="space-y-6 pt-6">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        <div>
                            <p className="text-sm font-semibold text-primary">
                                Estadísticas de {getMonthOptions().find(m => m.value === selectedMonth)?.label} {selectedYear}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {selectedUser === 'all'
                                    ? 'Todos los usuarios'
                                    : getUserName(selectedUser)
                                }
                            </p>
                        </div>
                    </div>
                </div>
                {/* Dashboard de Estadísticas */}
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    {/* Total Registros */}
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                {isUserSelected ? 'Registros Usuario' : 'Total Registros'}
                            </CardTitle>
                            <div className="rounded-lg bg-primary/10 p-2">
                                <FileStack className="h-4 w-4 text-primary" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.totalRecords}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {isUserSelected ? `De ${getUserName(selectedUser)}` : 'En el sistema'}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Usuarios (solo global) */}
                    {!isUserSelected && (
                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Usuarios</CardTitle>
                                <div className="rounded-lg bg-blue-500/10 p-2">
                                    <Users className="h-4 w-4 text-blue-500" />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{globalStats.totalUsers}</div>
                                <p className="text-xs text-muted-foreground mt-1">Con visitas registradas</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Completadas */}
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Completadas</CardTitle>
                            <div className="rounded-lg bg-green-500/10 p-2">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.completed}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {displayStats.totalRecords > 0
                                    ? `${Math.round((displayStats.completed / displayStats.totalRecords) * 100)}% del total`
                                    : '0% del total'}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Pendientes */}
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
                            <div className="rounded-lg bg-yellow-500/10 p-2">
                                <AlertCircle className="h-4 w-4 text-yellow-500" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.pending}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {displayStats.totalRecords > 0
                                    ? `${Math.round((displayStats.pending / displayStats.totalRecords) * 100)}% del total`
                                    : '0% del total'}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Canceladas */}
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Canceladas</CardTitle>
                            <div className="rounded-lg bg-red-500/10 p-2">
                                <XCircle className="h-4 w-4 text-red-500" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.canceled}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {displayStats.totalRecords > 0
                                    ? `${Math.round((displayStats.canceled / displayStats.totalRecords) * 100)}% del total`
                                    : '0% del total'}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Header con filtros */}
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-3xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                                Gestión de Visitas
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {total} {total === 1 ? 'visita encontrada' : 'visitas encontradas'}
                            </p>
                        </div>
                    </div>

                    {/* Filtros */}
                    <div className="flex flex-wrap gap-3">
                        <div className="w-full sm:flex-1 sm:max-w-md">
                            <Input
                                type="text"
                                placeholder="Buscar por organización, objetivo..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full"
                            />
                        </div>


                        {/* select de estado */}

                        <Select
                            value={statusFilter}
                            onValueChange={(value) => {
                                setStatusFilter(value);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-45">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                <SelectItem value="pending">Pendiente</SelectItem>
                                <SelectItem value="completed">Completada</SelectItem>
                                <SelectItem value="canceled">Cancelada</SelectItem>
                            </SelectContent>
                        </Select>
                        {/* select de usuarios */}

                        {usersError ? (
                            <div className="flex items-center gap-2">
                                <Alert variant="destructive" className="py-2 px-3 flex-1">
                                    <AlertDescription className="text-xs">{usersError}</AlertDescription>
                                </Alert>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={loadUsers}
                                    disabled={loadingUsers}
                                    className="shrink-0"
                                >
                                    {loadingUsers ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        'Reintentar'
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <Select
                                value={selectedUser}
                                onValueChange={(value) => {
                                    setSelectedUser(value);
                                    setPage(1);
                                }}
                                disabled={loadingUsers}
                            >
                                <SelectTrigger className="w-50">
                                    <SelectValue placeholder="Usuario" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los usuarios</SelectItem>
                                    {users.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            {user.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}


                        {/* select del año  */}
                        <Select value={selectedYear} onValueChange={(value) => {
                            setSelectedYear(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-32.5">
                                <SelectValue placeholder="Año" />
                            </SelectTrigger>
                            <SelectContent>
                                {getYearOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* select del mes */}

                        <Select value={selectedMonth} onValueChange={(value) => {
                            setSelectedMonth(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-35">
                                <SelectValue placeholder="Mes" />
                            </SelectTrigger>
                            <SelectContent>
                                {getMonthOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Grid de visitas */}
                {visits.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="rounded-full bg-muted p-6">
                                    <Calendar className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-lg font-medium">No hay visitas registradas</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {searchQuery
                                            ? 'Intenta ajustar los filtros de búsqueda'
                                            : 'No se encontraron visitas con los filtros seleccionados'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            {visits.map((visit) => {
                                const startDate = visit.visitDates[1] ? new Date(visit.visitDates[1]) : null;
                                const endDate = visit.visitDates[2] ? new Date(visit.visitDates[2]) : null;

                                return (
                                    <Card
                                        key={visit.$id}
                                        className="group hover:shadow-xl duration-300 cursor-pointer overflow-hidden border-2"
                                        onClick={() => handleViewDetail(visit)}
                                    >
                                        <CardHeader className="pb-0">
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                                                        <Building2 className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <h3 className="font-semibold text-base line-clamp-1 leading-tight">
                                                        {visit.organizationName}
                                                    </h3>
                                                </div>
                                                {getStatusBadge(visit.status)}
                                            </div>

                                            <Badge
                                                variant="outline"
                                                className="text-xs max-w-35 line-clamp-2 bg-slate-100 dark:bg-slate-600 "
                                            >
                                                {visit.branch ?? 'Matriz'}
                                            </Badge>

                                            {visit.usersId && visit.usersId.length > 0 && (
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs flex items-center gap-1 w-fit bg-blue-50 text-blue-700 dark:text-blue-7😄00 border-blue-200"
                                                >
                                                    <Users className="w-3 h-3" />
                                                    {visit.usersId.length} {visit.usersId.length === 1 ? 'acompañante' : 'acompañantes'}
                                                </Badge>
                                            )}

                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Users className="h-4 w-4" />
                                                <span className="font-medium">{getUserName(visit.userId)}</span>
                                            </div>

                                            <p className="text-sm text-muted-foreground line-clamp-2 min-h-10 mt-2">
                                                {visit.visitPurpose || 'Sin objetivo especificado'}
                                            </p>
                                        </CardHeader>

                                        <CardContent className="pt-1 pb-1 space-y-3">
                                            {startDate && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <div className="rounded-md bg-blue-500/10 p-1.5">
                                                        <Calendar className="h-3.5 w-3.5 text-blue-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-muted-foreground">Fecha</p>
                                                        <p className="font-medium truncate">{formatDate(startDate.toISOString())}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {startDate && endDate && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <div className="rounded-md bg-purple-500/10 p-1.5">
                                                        <Clock className="h-3.5 w-3.5 text-purple-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-muted-foreground">Horario</p>
                                                        <p className="font-medium">
                                                            {formatTime(startDate.toISOString())} - {formatTime(endDate.toISOString())}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}


                                            <div className="flex items-center justify-between">
                                                <p className="text-xs font-medium text-muted-foreground">Acuerdos</p>
                                                <span className="inline-flex items-center justify-center rounded-full bg-orange-500/20 px-2 py-0.5 text-xs font-semibold text-orange-700">
                                                    {visit.agreements?.length || 0}
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <p className="text-xs font-medium text-muted-foreground">Observaciones</p>
                                                <span className="inline-flex items-center justify-center rounded-full bg-cyan-500/20 px-2 py-0.5 text-xs font-semibold text-cyan-700">
                                                    {visit.observations?.length || 0}
                                                </span>
                                            </div>



                                            <div className="pt-2 border-t flex justify-between items-center gap-1">
                                                <p className="text-xs text-muted-foreground">
                                                    {formatDate(visit.$createdAt)}
                                                </p>
                                                <div className="flex gap-1">
                                                    {userRoles?.isAdmin && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 cursor-pointer text-violet-600 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-900/20"
                                                            onClick={(e) => handleOpenEdit(visit, e)}
                                                        >
                                                            <Pencil className="h-3.5 w-3.5 mr-1" />
                                                            Editar
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 cursor-pointer"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewDetail(visit);
                                                        }}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        Ver más
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1 || loading}
                                    className="cursor-pointer"
                                >
                                    Anterior
                                </Button>
                                <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-md">
                                    <span className="text-sm font-medium">Página {page}</span>
                                    <span className="text-muted-foreground">de</span>
                                    <span className="text-sm font-medium">{totalPages}</span>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || loading}
                                    className="cursor-pointer"
                                >
                                    Siguiente
                                </Button>
                            </div>
                        )}
                    </>
                )}

                {/* Dialog de edición — solo admin */}
                <Dialog open={!!editingVisit} onOpenChange={(open) => { if (!open) setEditingVisit(null); }}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <ShieldCheck className="h-5 w-5 text-violet-500" />
                                Editar visita
                            </DialogTitle>
                            <DialogDescription>
                                {editingVisit?.organizationName}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-5 py-2">

                            {/* Estado */}
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Estado</label>
                                <Select
                                    value={editForm.status}
                                    onValueChange={v => setEditForm(f => ({ ...f, status: v }))}
                                >
                                    <SelectTrigger className="cursor-pointer">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">Pendiente</SelectItem>
                                        <SelectItem value="completed">Completada</SelectItem>
                                        <SelectItem value="canceled">Cancelada</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Fecha */}
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Fecha de visita</label>
                                <Input
                                    type="date"
                                    value={editForm.visitDate}
                                    onChange={e => setEditForm(f => ({ ...f, visitDate: e.target.value }))}
                                    className="cursor-pointer"
                                />
                            </div>

                            {/* Horario */}
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Horario</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <p className="text-xs text-muted-foreground">Hora entrada</p>
                                        <Input
                                            type="time"
                                            value={editForm.startTime}
                                            onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))}
                                            className="cursor-pointer"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs text-muted-foreground">Hora salida</p>
                                        <Input
                                            type="time"
                                            value={editForm.endTime}
                                            onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))}
                                            className="cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Objetivo */}
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Objetivo de visita</label>
                                <Textarea
                                    value={editForm.visitPurpose}
                                    onChange={e => setEditForm(f => ({ ...f, visitPurpose: e.target.value }))}
                                    placeholder="Ingresa el objetivo..."
                                    className="min-h-20 resize-none"
                                />
                            </div>

                            {/* Sucursal */}
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Sucursal</label>
                                <Input
                                    value={editForm.branch}
                                    onChange={e => setEditForm(f => ({ ...f, branch: e.target.value }))}
                                    placeholder="Ej: Matriz, Norte, Sur..."
                                />
                            </div>

                            {/* Acompañantes */}
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">
                                    Acompañantes
                                    {editForm.companions.length > 0 && (
                                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                                            ({editForm.companions.length} seleccionado{editForm.companions.length !== 1 ? 's' : ''})
                                        </span>
                                    )}
                                </label>
                                {users.filter(u => u.id !== editingVisit?.userId).length === 0 ? (
                                    <p className="text-sm text-muted-foreground italic">Sin otros usuarios disponibles</p>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto rounded-md border p-2">
                                        {users
                                            .filter(u => u.id !== editingVisit?.userId)
                                            .map(u => {
                                                const name = u.name || [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || 'Sin nombre';
                                                const selected = editForm.companions.includes(u.id);
                                                return (
                                                    <button
                                                        key={u.id}
                                                        type="button"
                                                        onClick={() => setEditForm(f => ({
                                                            ...f,
                                                            companions: selected
                                                                ? f.companions.filter(id => id !== u.id)
                                                                : [...f.companions, u.id],
                                                        }))}
                                                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                                                            selected
                                                                ? 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-700'
                                                                : 'border-border bg-background text-muted-foreground hover:bg-muted'
                                                        }`}
                                                    >
                                                        {name}
                                                    </button>
                                                );
                                            })
                                        }
                                    </div>
                                )}
                            </div>

                            {editError && (
                                <Alert variant="destructive">
                                    <AlertDescription>{editError}</AlertDescription>
                                </Alert>
                            )}
                        </div>

                        <div className="flex gap-2 justify-end pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setEditingVisit(null)}
                                disabled={isSaving}
                                className="cursor-pointer"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleSaveEdit}
                                disabled={isSaving}
                                className="cursor-pointer bg-violet-600 hover:bg-violet-700 text-white"
                            >
                                {isSaving
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : 'Guardar cambios'
                                }
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Modal de detalle */}
                <Dialog open={showDetail} onOpenChange={setShowDetail}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Building2 className="h-5 w-5" />
                                {selectedVisit?.organizationName}
                            </DialogTitle>
                            <DialogDescription>
                                <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    {selectedVisit && getUserName(selectedVisit.userId)}
                                </div>
                                {selectedVisit?.contactPerson && (
                                    <div className="mt-1">
                                        {selectedVisit.contactPerson} {selectedVisit.contactRole && `· ${selectedVisit.contactRole}`}
                                    </div>
                                )}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="overflow-y-auto max-h-[calc(80vh-120px)] pr-2">
                            {!selectedVisit ? (
                                <div className="py-12 text-center text-muted-foreground">Cargando...</div>
                            ) : (
                                <Tabs defaultValue="general" className="w-full">
                                    <TabsList className="grid w-full grid-cols-3">
                                        <TabsTrigger value="general">General</TabsTrigger>
                                        <TabsTrigger value="detalles">
                                            Detalles
                                            <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                {(selectedVisit.agreements?.length || 0) + (selectedVisit.observations?.length || 0)}
                                            </span>
                                        </TabsTrigger>
                                        <TabsTrigger value="firma">Firma</TabsTrigger>
                                    </TabsList>

                                    {/* TAB GENERAL */}
                                    <TabsContent value="general" className="space-y-4 mt-4 font-outfit">
                                        <div className="grid gap-4">
                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                <label className="text-sm font-medium text-muted-foreground">Usuario:</label>
                                                <p className="text-sm font-medium wrap-break-word">{getUserName(selectedVisit.userId)}</p>
                                            </div>


                                            {selectedVisit.usersId && selectedVisit.usersId.length > 0 && (
                                                <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                    <label className="text-sm font-medium text-muted-foreground">Acompañantes:</label>
                                                    <div className="flex flex-wrap gap-1">
                                                        {selectedVisit.usersId.map((id) => (
                                                            <Badge key={id} variant="outline" className="text-xs">
                                                                {getUserName(id)}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                <label className="text-sm font-medium text-muted-foreground">Organización:</label>
                                                <div className="text-sm min-w-0">
                                                    <p className="font-medium wrap-break-word">{selectedVisit.organizationName}</p>
                                                    {selectedVisit.organizationCity && (
                                                        <p className="text-muted-foreground wrap-break-word">{selectedVisit.organizationCity}</p>
                                                    )}
                                                    {selectedVisit.organizationAddress && (
                                                        <p className="text-muted-foreground wrap-break-word text-xs mt-1">{selectedVisit.organizationAddress}</p>
                                                    )}
                                                </div>
                                            </div>

                                            {selectedVisit.contactPerson && (
                                                <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                    <label className="text-sm font-medium text-muted-foreground">Contacto:</label>
                                                    <div className="text-sm min-w-0">
                                                        <p className="font-medium wrap-break-word">{selectedVisit.contactPerson}</p>
                                                        {selectedVisit.contactRole && (
                                                            <p className="text-muted-foreground wrap-break-word">{selectedVisit.contactRole}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                <label className="text-sm font-medium text-muted-foreground">Sucursal:</label>
                                                {selectedVisit.branch ?? 'Matriz'}

                                            </div>


                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                <label className="text-sm font-medium text-muted-foreground">Estado:</label>
                                                {getStatusBadge(selectedVisit.status)}
                                            </div>

                                            {selectedVisit.visitPurpose && (
                                                <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                    <label className="text-sm font-medium text-muted-foreground">Objetivo:</label>
                                                    <p className="text-sm leading-relaxed wrap-break-word whitespace-pre-wrap min-w-0">
                                                        {selectedVisit.visitPurpose}
                                                    </p>
                                                </div>
                                            )}

                                            {selectedVisit.visitDates[1] && (
                                                <>
                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                        <label className="text-sm font-medium text-muted-foreground">Fecha:</label>
                                                        <p className="text-sm font-medium">
                                                            {formatDate(selectedVisit.visitDates[1])}
                                                        </p>
                                                    </div>

                                                    {selectedVisit.visitDates[2] && (
                                                        <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                            <label className="text-sm font-medium text-muted-foreground">Horario:</label>
                                                            <p className="text-sm font-medium">
                                                                {formatTime(selectedVisit.visitDates[1])} - {formatTime(selectedVisit.visitDates[2])}
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {selectedVisit.userComment && (
                                                <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                    <label className="text-sm font-medium text-muted-foreground">Comentario:</label>
                                                    <p className="text-sm leading-relaxed wrap-break-word whitespace-pre-wrap min-w-0 bg-muted/30  rounded-lg">
                                                        {selectedVisit.userComment}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start pt-2 border-t">
                                                <label className="text-sm font-medium text-muted-foreground">Fecha de registro:</label>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDate(selectedVisit.$createdAt)} a las {formatTime(selectedVisit.$createdAt)}
                                                </p>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    {/* TAB DETALLES */}
                                    <TabsContent value="detalles" className="space-y-4 mt-4 overflow-x-hidden">
                                        <div className="space-y-6">
                                            {/* Acuerdos */}
                                            <div className="overflow-x-hidden">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <h3 className="text-sm font-semibold">Acuerdos</h3>
                                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                        {selectedVisit.agreements?.length || 0}
                                                    </span>
                                                </div>

                                                {selectedVisit.agreements && selectedVisit.agreements.length > 0 ? (
                                                    <div className="max-h-100 overflow-y-auto overflow-x-hidden space-y-2 pr-2">
                                                        {selectedVisit.agreements.map((agreement: string, index: number) => (
                                                            <div
                                                                key={index}
                                                                className="p-3 rounded-lg bg-muted/30 border border-border/50"
                                                            >
                                                                <div className="flex gap-3">
                                                                    <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">
                                                                        {index + 1}.
                                                                    </span>
                                                                    <p className="text-sm leading-relaxed min-w-0 wrap-break-word whitespace-pre-wrap">
                                                                        {agreement}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground italic">Sin acuerdos registrados</p>
                                                )}
                                            </div>

                                            {/* Observaciones */}
                                            <div className="overflow-x-hidden">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <h3 className="text-sm font-semibold">Observaciones</h3>
                                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                        {selectedVisit.observations?.length || 0}
                                                    </span>
                                                </div>

                                                {selectedVisit.observations && selectedVisit.observations.length > 0 ? (
                                                    <div className="max-h-100 overflow-y-auto overflow-x-hidden space-y-2 pr-2">
                                                        {selectedVisit.observations.map((observation: string, index: number) => (
                                                            <div
                                                                key={index}
                                                                className="p-3 rounded-lg bg-muted/30 border border-border/50"
                                                            >
                                                                <div className="flex gap-3">
                                                                    <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">
                                                                        {index + 1}.
                                                                    </span>
                                                                    <p className="text-sm leading-relaxed min-w-0 wrap-break-word whitespace-pre-wrap">
                                                                        {observation}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground italic">Sin observaciones registradas</p>
                                                )}
                                            </div>
                                        </div>
                                    </TabsContent>

                                    {/* TAB FIRMA */}
                                    <TabsContent value="firma" className="space-y-4 mt-4">
                                        {selectedVisit.signature && selectedVisit.signature.length > 0 ? (
                                            <div className="space-y-4">
                                                <div className="flex justify-center p-4 bg-muted/30 rounded-lg border">
                                                    <img
                                                        src={getSignatureUrl(selectedVisit.signature[0])}
                                                        alt="Firma"
                                                        className="max-w-full h-auto max-h-75 object-contain"
                                                    />
                                                </div>

                                                <div className="grid gap-3">
                                                    {selectedVisit.signature[2] && (
                                                        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                                                            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                                                            <div>
                                                                <p className="text-xs text-muted-foreground mb-1">Fecha y hora</p>
                                                                <p className="text-sm font-medium">
                                                                    {formatDate(selectedVisit.signature[2])}
                                                                    {" a las "}
                                                                    {formatTime(selectedVisit.signature[2])}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {selectedVisit.signature[1] && (
                                                        <Dialog>
                                                            <DialogTrigger asChild>
                                                                <Button className="flex cursor-pointer" variant={'outline'}>
                                                                    <MapPin className="h-5 w-5 text-blue-500 mt-0.5" />Ubicación de la firma

                                                                </Button>
                                                            </DialogTrigger>

                                                            <DialogContent className="w-full max-w-2xl">
                                                                <DialogHeader>
                                                                    <DialogTitle>Ubicación de la Firma</DialogTitle>
                                                                </DialogHeader>

                                                                <MapboxMap coords={parseCoords(selectedVisit.signature[1])} height="h-96" />
                                                            </DialogContent>
                                                        </Dialog>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-muted-foreground">
                                                <p className="italic">Sin firma registrada</p>
                                            </div>
                                        )}
                                    </TabsContent>
                                </Tabs>
                            )}
                        </div>
                    </DialogContent>
                </Dialog >
            </div >
        </>
    );
};

export default AdminVisitsList;